import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { SMLUpgradeFeature } from '../../../config/music24PlanCapabilities';
import { useStreamingShortVideo } from '../../hooks/useStreamingShortVideo';
import { useStreamingSearchParam } from '../../hooks/useStreamingSearchParam';
import { hasUgcNumbers, pickedOption } from '../../periodSections';
import { sourceLook } from '../../streamingSources';
import { shortVideoMessages as messages } from '../../shortVideoMessages';
import { streamingPeriodMessages } from '../../streamingPeriodMessages';
import { StreamingPlanLockCard } from '../../parts/StreamingPlanLockCard';
import { SectionSourceSwitch } from '../../period/SectionSourceSwitch';
import { isUgcPlatform } from './ugcPlatforms';
import { UgcPlatformRow } from './UgcPlatformRow';
import { UgcTiles } from './UgcTiles';
import { UgcDayBars } from './UgcDayBars';
import { UgcPlatformTable } from './UgcPlatformTable';
import { UgcTrackTable } from './UgcTrackTable';
import { ShortVideoNone } from './ShortVideoNone';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import shared from '../../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ShortVideoBodyProps {
  accountId: number;
  days: number;
}

/** The read of Short video: the switch, the platform row and the blocks. The platform filters every block. */
const ShortVideoBodyComponent: React.FC<ShortVideoBodyProps> = ({ accountId, days }) => {
  const intl = useIntl();
  const [source, setSource] = useStreamingSearchParam('source');
  const [platformParam, setPlatform] = useStreamingSearchParam('platform');
  const platform = isUgcPlatform(platformParam) ? platformParam : undefined;
  const { data, isLoading, error, retry } = useStreamingShortVideo({ accountId, days, source, platform, skip: false });

  if (isLoading) {
    return <LoadingView />;
  }
  if (error !== undefined) {
    return <ErrorView error={error} retry={retry} />;
  }
  if (data === undefined) {
    return null;
  }
  if (data.locked === 'plan') {
    return (
      <StreamingPlanLockCard
        feature={SMLUpgradeFeature.streamingSplit}
        title={streamingPeriodMessages.planTitle}
        testId="streaming-shortVideo-plan-lock"
      />
    );
  }

  const picked = pickedOption(data.sources, data.source);
  const sourceName = picked === undefined ? '' : sourceLook(picked.integrationType).name;

  return (
    <div className={shared.page} data-testid="streaming-section-shortVideo">
      <SectionSourceSwitch options={data.sources} picked={data.source} onPick={setSource} />
      <UgcPlatformRow platforms={data.platforms} picked={data.platform} onPick={setPlatform} />
      <p className={shared.faint}>{intl.formatMessage(messages.intro, { source: sourceName })}</p>
      {!data.available ? (
        <p className={shared.faint}>{intl.formatMessage(streamingPeriodMessages.notAvailable)}</p>
      ) : null}
      {hasUgcNumbers(data.totals) ? (
        <>
          <UgcTiles totals={data.totals} previous={data.previous} />
          {data.series.length > 0 ? <UgcDayBars series={data.series} /> : null}
          <UgcPlatformTable platforms={data.platforms} />
          {data.tracks.length > 0 ? <UgcTrackTable tracks={data.tracks} sourceName={sourceName} /> : null}
        </>
      ) : (
        <ShortVideoNone answer={data} sourceName={sourceName} days={days} onShowAll={() => setPlatform(undefined)} />
      )}
    </div>
  );
};

export const ShortVideoBody = TypedMemo(ShortVideoBodyComponent);
